import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import AppLayout from "./components/AppLayout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Technician from "./pages/Technician";
import Vehicles from "./pages/Vehicles";
import VehicleDetail from "./pages/VehicleDetail";
import Tyres from "./pages/Tyres";
import TyrePassport from "./pages/TyrePassport";
import Punctures from "./pages/Punctures";
import Inspections from "./pages/Inspections";
import Alerts from "./pages/Alerts";
import Reports from "./pages/Reports";
import Users from "./pages/Users";
import Scan from "./pages/Scan";
import Activity from "./pages/Activity";
import Customers from "./pages/Customers";
import SettingsPage from "./pages/SettingsPage";
import Competition from "./pages/Competition";
import CompetitionDetail from "./pages/CompetitionDetail";
import Guide from "./pages/Guide";
import { Wrench, Buildings } from "@phosphor-icons/react";
import "./App.css";

function Loader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-950">
      <div className="flex h-12 w-12 animate-pulse items-center justify-center rounded-sm bg-amber-500">
        <Wrench size={26} weight="bold" className="text-zinc-950" />
      </div>
    </div>
  );
}

function CustomerGate() {
  const { customers, setCurrentCustomer, hasRole, logout } = useAuth();
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-zinc-950 p-6" data-testid="customer-gate">
      <div className="mb-8 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-amber-500"><Buildings size={22} weight="bold" className="text-zinc-950" /></div>
        <div>
          <div className="font-display text-xl font-extrabold tracking-tight">Select Customer</div>
          <div className="text-sm text-zinc-500">Choose which customer's operation to work on</div>
        </div>
      </div>
      <div className="grid w-full max-w-lg grid-cols-1 gap-3 sm:grid-cols-2">
        {customers.map((c) => (
          <button key={c.id} data-testid={`gate-customer-${c.id}`} onClick={() => setCurrentCustomer(c)}
            className="group rounded-sm border border-zinc-800 bg-zinc-900 p-4 text-left ring-1 ring-white/5 transition-colors hover:border-amber-500 hover:bg-zinc-800/60">
            <div className="font-display text-base font-bold text-zinc-50 group-hover:text-amber-400">{c.name}</div>
            <div className="mt-1 text-xs text-zinc-500">{c.site_count ?? 0} mine site(s)</div>
          </button>
        ))}
        {customers.length === 0 && (
          <div className="col-span-full rounded-sm border border-dashed border-zinc-800 p-6 text-center text-sm text-zinc-500">
            No customers yet.{hasRole("admin") ? " Add one from the Customers page after logging in." : " Ask your admin to add a customer."}
          </div>
        )}
      </div>
      <button onClick={logout} className="mt-8 text-xs text-zinc-500 hover:text-rose-400">Sign out</button>
    </div>
  );
}

function Protected({ children }) {
  const { user, loading, currentCustomer } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!currentCustomer) return <CustomerGate />;
  return <AppLayout>{children}</AppLayout>;
}

function Home() {
  const { user } = useAuth();
  if (user?.role === "technician") return <Navigate to="/technician" replace />;
  return <Dashboard />;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Protected><Home /></Protected>} />
      <Route path="/technician" element={<Protected><Technician /></Protected>} />
      <Route path="/vehicles" element={<Protected><Vehicles /></Protected>} />
      <Route path="/vehicles/:id" element={<Protected><VehicleDetail /></Protected>} />
      <Route path="/tyres" element={<Protected><Tyres /></Protected>} />
      <Route path="/tyres/:id" element={<Protected><TyrePassport /></Protected>} />
      <Route path="/punctures" element={<Protected><Punctures /></Protected>} />
      <Route path="/inspections" element={<Protected><Inspections /></Protected>} />
      <Route path="/alerts" element={<Protected><Alerts /></Protected>} />
      <Route path="/reports" element={<Protected><Reports /></Protected>} />
      <Route path="/activity" element={<Protected><Activity /></Protected>} />
      <Route path="/users" element={<Protected><Users /></Protected>} />
      <Route path="/customers" element={<Protected><Customers /></Protected>} />
      <Route path="/settings" element={<Protected><SettingsPage /></Protected>} />
      <Route path="/competition" element={<Protected><Competition /></Protected>} />
      <Route path="/competition/:id" element={<Protected><CompetitionDetail /></Protected>} />
      <Route path="/guide" element={<Protected><Guide /></Protected>} />
      <Route path="/scan" element={<Protected><Scan /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter><AppRouter /></BrowserRouter>
        <Toaster position="top-right" theme="dark" richColors />
      </AuthProvider>
    </div>
  );
}
